<script setup lang="ts">
import { useDisableComposable, DisabledPropsInterface } from '@src/application/composables/disabled-composable.js';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import Tooltip from '@components/tooltip.vue';
import { ref } from 'vue';
import { useSingleRefComposable } from '@src/application/composables/single-ref-composable.js';
import Icon from '@components/icons/icon.vue';
import type { TooltipPositions } from '@components/tooltip-types.js';
import { ActionInterface } from '@components/context-menu-action-interface.js';
import Text from '@components/text.vue';
import { IconType } from '@components/icons/icon-types.ts';

const { setSingleTemplateRef, getSingleTemplateRef } = useSingleRefComposable();

interface IconTooltipConfigInterface {
    icon: IconType,
    tooltip: string,
    position: TooltipPositions,
}
interface ContextMenuInterface {
    actions: Array<ActionInterface>|null,
    iconTooltipConfig?: IconTooltipConfigInterface|null,
    position?: 'right'|'left'|'bottom_right'|'bottom_left',
    context?: 'primary'|'default',
    label?: string|null,
    hideLabelLowScreen?: boolean,
    fullHeight?: boolean,
}

withDefaults(defineProps<DisabledPropsInterface & ContextMenuInterface>(), {
    position: 'right',
    context: 'default',
    iconTooltipConfig: null,
    label: null,
    hideLabelLowScreen: false,
    fullHeight: false,
});

const { disabled } = useDisableComposable();
const overlay = ref(false);

function showOverlay() {
    if (disabled.value === true) {
        return;
    }
    const contextMenuRef = getSingleTemplateRef() as HTMLElement|null;
    if (contextMenuRef === null) {
        return;
    }
    contextMenuRef.focus();
    overlay.value = true;
}

function removeOverlay() {
    overlay.value = false;
}
async function triggerAction(action: ActionInterface) {
    const result = await action.callback();
    overlay.value = false;
    emits('action-triggered', action);

    return result;
}

const emits = defineEmits<((event: 'action-triggered', action: ActionInterface) => void)>();
</script>

<template>
    <div
        :ref="setSingleTemplateRef"
        :class="[$style.context_menu, disabled ? $style.disabled : '', $style[context], fullHeight ? $style.full_height : '']"
        tabindex="-1"
        @mousedown.prevent.stop="overlay ? removeOverlay() : showOverlay()"
        @blur="removeOverlay"
    >
        <slot>
            <div v-if="iconTooltipConfig !== null" :class="$style.trigger__wrapper">
                <Tooltip
                    v-if="!overlay && !disabled"
                    :tooltip="iconTooltipConfig.tooltip"
                    :position="iconTooltipConfig.position"
                    :change-text-color-on-hover="context !== 'primary'"
                    context="primary"
                    expansion
                >
                    <HorizontalAlignChildrenWrapper gap="1" :wrap-on-small-screen="false" :center-children="true">
                        <Icon
                            :icon="iconTooltipConfig.icon"
                            :icon-color="context === 'primary' ? 'white' : 'default'"
                            :class="!label ? $style.icon_only : ''"
                        />
                        <p v-if="label !== null" :class="hideLabelLowScreen ? $style.hide_label_low_screen : ''">{{ label }}</p>
                    </HorizontalAlignChildrenWrapper>
                </Tooltip>
                <HorizontalAlignChildrenWrapper
                    v-else
                    gap="1"
                    :wrap-on-small-screen="false"
                    :center-children="true"
                >
                    <Icon
                        :icon="iconTooltipConfig.icon"
                        :icon-color="disabled ? 'grey' : (context === 'primary' ? 'white' : 'primary')"
                        :class="!label ? $style.icon_only : ''"
                    />
                    <p v-if="label !== null" :class="hideLabelLowScreen ? $style.hide_label_low_screen : ''">{{ label }}</p>
                </HorizontalAlignChildrenWrapper>
            </div>
            <p v-else-if="label !== null">{{ label }}</p>
        </slot>
        <ul v-if="overlay" :class="[$style.overlay, $style[position]]">
            <li
                v-for="action in actions"
                :class="[$style.list_entry, (action.disabled ? $style.disabled : null)]"
                @mousedown.prevent.stop="action.disabled ? null : triggerAction(action)"
            >
                <Text :icon="action.icon ? action.icon : false"> {{ action.label }}</Text>
            </li>
        </ul>
    </div>
</template>

<style lang="scss" module>
@use '@text' as *;
@use '@borders' as *;
@use '@backgrounds' as *;

$disabled-button-text: #a8a8a8;
$disabled-button-background: #e6e6e6;

.context_menu {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: inherit;
    cursor: pointer;

    > * { cursor: inherit; }
    &.disabled {
        cursor: not-allowed;
    }
    &.full_height {
        align-items: stretch;
    }
    &.primary {
        background-color: $primary-background;
        &:hover {
            background-color: $primary-background-hover;
        }
        &.disabled {
            background-color: #e6e6e6;
        }
        .trigger__wrapper > * {
            padding: .5em;
        }
    }

    @media (max-width: 33em) {
        .hide_label_low_screen {
            display: none;
        }
    }
}
.trigger__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;

    .icon_only {
        min-width: 1em;
        * {
            width: inherit;
        }
    }
}
.overlay {
    position: absolute;
    z-index: 9;
    padding: 2px;
    color: $default-text-color;
    text-align: left;
    background-color: #fff;
    border-radius: 3px;

    &::before {
        position: absolute;
        width: 0;
        height: 0;
        content: '';
        border-style: solid;
    }
    &.right {
        top: calc(50% - 20px);
        left: calc(100% + 10px);
        box-shadow: -5px 4px 20px -4px rgba(0, 0, 0, 25%);

        &::before {
            top: 10px;
            left: -9px;
            border-color: transparent #fff transparent transparent;
            border-width: 10px 10px 10px 0;

        }
    }
    &.left {
        top: calc(50% - 20px);
        right: calc(100% + 10px);
        box-shadow: 5px 4px 20px 4px rgba(0, 0, 0, 25%);

        &::before {
            top: 10px;
            right: -9px;
            border-color: transparent transparent transparent #fff;
            border-width: 10px 0 10px 10px;
        }
    }
    &.bottom_right {
        top: calc(50% + 20px);
        left: calc(50% - 17px);
        box-shadow: 2px 4px 15px 0 rgba(0, 0, 0, 25%);

        &::before {
            top: -8px;
            left: 8px;
            border-color: transparent transparent #fff transparent;
            border-width: 0 10px 10px 10px;
        }
    }
    &.bottom_left {
        top: calc(50% + 20px);
        right: calc(50% - 17px);
        box-shadow: 2px 4px 15px 0 rgba(0, 0, 0, 25%);

        &::before {
            top: -8px;
            right: 8px;
            border-color: transparent transparent #fff transparent;
            border-width: 0 10px 10px 10px;
        }
    }

    .list_entry {
        padding: 12px;
        white-space: nowrap;

        &.disabled {
            color: $disabled-button-text;
            cursor: not-allowed;
            background-color: $disabled-button-background;
            border-color: $disabled-button-background;
        }
        * { color: inherit }

        &:not(:last-child) {
            border-bottom: thin solid #d4d9db;
        }
        &:hover:not(.disabled) {
            color: #fff;
            cursor: pointer;
            background-color: $primary-background;

        }
        &:first-child {
            border-radius: 3px 3px 0 0;
        }
        &:last-child {
            border-radius: 0 0 3px 3px;
        }
        &:only-child {
            border-radius: 3px;
        }
    }
}
</style>
